import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { CodeDTO } from 'src/app/shared/models/dtos/code-dto/code-dto';
import { RedeemRequestDTO } from 'src/app/shared/models/dtos/requests/redeem-request-dto/redeem-request-dto';
import { Coupon } from 'src/app/shared/models/dtos/responses/coupon/coupon';
import { PointResponseDTO } from 'src/app/shared/models/dtos/responses/point-response-dto/point-response-dto';
import { PointSummaryDTO } from 'src/app/shared/models/dtos/responses/point-summary/point-summary-dto';
import { RewardResponseDTO } from 'src/app/shared/models/dtos/responses/reward-response-dto/reward-response-dto';
import { ValidatePointCodeResponseDTO } from 'src/app/shared/models/dtos/responses/validate-point-code-response-dto/validate-point-code-response-dto';
import { TransactionDetail } from 'src/app/shared/models/transaction-detail/transaction-detail';
import { toAPIDate } from 'src/app/utils/date';
import { TransactionGroup } from 'src/app/shared/models/transaction/transaction-group';

@Injectable({
  providedIn: 'root',
})
/**
 * Service to interact with LnR's Reward Service
 */
export class RewardService {
  private backendAuthURL = '/rwd/api/a';
  private backendURL = '/rwd/api';

  /**
   * @constructor
   */
  constructor(private httpClient: HttpClient) {}

  /**
   * Get all rewards
   * @return {Observable<RewardResponseDTO>}
   */
  getAllRewards(): Observable<RewardResponseDTO[]> {
    return this.httpClient.get<RewardResponseDTO[]>(
      `${this.backendAuthURL}/reward?page=0&pageSize=20`
    );
  }

  /**
   * Get a reward by passind the id
   * @param {number} id
   * @return {Observable<RewardResponseDTO>}
   */
  getReward(id: number): Observable<RewardResponseDTO> {
    return this.httpClient.get<RewardResponseDTO>(
      `${this.backendAuthURL}/reward/${id}`
    );
  }

  /**
   * Contacts backend asking to redeem a reward
   * @param {number} id id of reward to be redeemed
   * @param {RedeemRequestDTO} redeemRequestDTO
   * @return {Observable<Coupon>}
   */
  redeemReward(
    id: number,
    redeemRequestDTO: RedeemRequestDTO
  ): Observable<Coupon> {
    const endpoint = `reward/redeem/${id}`;
    return this.httpClient.post<Coupon>(
      this.buildURL(endpoint),
      redeemRequestDTO
    );
  }

  /**
   * Gets current points of a business
   * @param {number} businessId
   * @return {Observable<PointResponseDTO[]>}
   */
  getPoints(businessId: number): Observable<PointResponseDTO[]> {
    const endpoint = `point/${businessId}`;
    return this.httpClient.get<PointResponseDTO[]>(this.buildURL(endpoint));
  }

  /**
   * Get a coupon by passing the id
   * @param {number} id
   * @return {Observable<CouponResponseDTO>}
   */
  getCoupon(id: number): Observable<Coupon> {
    return this.httpClient
      .get<Coupon>(`${this.backendAuthURL}/coupon/${id}`)
      .pipe(
        map((result) => {
          result.rewardDescription = result.rewardDescription?.replace(
            /\\n/g,
            '\n'
          );
          return result;
        })
      );
  }

  /**
   * Checks if an inserted point code is valid
   * @param {string} code
   * @return {Observable<ValidatePointCodeResponseDTO>}
   */
  validatePointCode(code: string): Observable<ValidatePointCodeResponseDTO> {
    const endpoint = 'point-code/validate';
    return this.httpClient.post<ValidatePointCodeResponseDTO>(
      this.buildURL(endpoint),
      {
        code,
      }
    );
  }

  /**
   * Rescues point codes
   * @param {CodeDTO[]} codes
   * @return {Observable<CodeDTO[]>}
   */
  addPoints(codes: CodeDTO[]): Observable<CodeDTO[]> {
    const endpoint = 'point/retrieve';
    return this.httpClient.post<CodeDTO[]>(this.buildURL(endpoint), {
      codes,
    });
  }

  /**
   * Get summary of points to be shown at point balance page
   * @param {number} familyId
   * @return {Observable<PointSummaryDTO>}
   */
  getPointSummary(familyId: number): Observable<PointSummaryDTO> {
    const endpoint = `point/business/summary/family/${familyId}`;

    return this.httpClient.get<PointSummaryDTO>(this.buildURL(endpoint));
  }

  /**
   * Get summary of points for all families
   * @return {Observable<PointSummaryDTO[]>}
   */
  getPointSummaryForAllFamilies(): Observable<PointSummaryDTO[]> {
    const endpoint = 'point/business/summary/all-families';

    return this.httpClient.get<PointSummaryDTO[]>(this.buildURL(endpoint));
  }

  /**
   * @param {Object} queryParams
   * @return {Observable<Transaction[]>}
   */
  getTransactions(queryParams: {
    page: number;
    pageSize: number;
    familyId: number;
    spend?: boolean;
    added?: boolean;
    expired?: boolean;
    startDate?: Date;
    endDate?: Date;
  }): Observable<TransactionGroup[]> {
    const endpoint = 'transaction';
    const params: any = queryParams as any;
    for (const param in params) {
      if (params[param] == null) {
        delete params[param];
      }
    }
    return this.httpClient
      .get<TransactionGroup[]>(this.buildURL(endpoint), {
        params,
      })
      .pipe(
        map((groups) =>
          groups.map((group) => {
            group.date = new Date(group.date + 'T00:00:00');
            return group;
          })
        )
      );
  }

  /**
   * @param {Object} filters
   * @return {Observable<string>}
   */
  createReport(filters: {
    includeRedeemed?: boolean;
    includeAdded?: boolean;
    includeExpired?: boolean;
    includeRemoved?: boolean;
    startDate?: Date;
    endDate?: Date;
    familyId?: number;
  }): Observable<string> {
    const startDate = filters.startDate
      ? toAPIDate(filters.startDate)
      : undefined;
    const endDate = filters.endDate ? toAPIDate(filters.endDate) : undefined;
    const endpoint = 'transaction/report';
    const params: any = {
      spend: filters.includeRedeemed,
      added: filters.includeAdded,
      expired: filters.includeExpired,
      removed: filters.includeRemoved,
      familyId: filters.familyId,
    };
    startDate && (params['startDate'] = startDate);
    endDate && (params['endDate'] = endDate);
    return this.httpClient.get(this.buildURL(endpoint), {
      params,
      responseType: 'text',
    });
  }

  /**
   * @param {Number} transactionId
   * @return {Observable<TransactionDetail>}
   */
  getTransactionDetail(transactionId: number): Observable<TransactionDetail> {
    const endpoint = `transaction/${transactionId}`;
    return this.httpClient.get<TransactionDetail>(this.buildURL(endpoint));
  }

  /**
   * @param {Object} queryParams
   * @return {Observable<RewardResponseDTO[]>}
   */
  getRewards(queryParams: {
    page: number;
    pageSize: number;
    sort: string;
    myPrinters?: string;
    opportunities?: boolean;
    cservice?: boolean;
  }): Observable<RewardResponseDTO[]> {
    const endpoint = 'reward';
    const params: any = queryParams as any;
    return this.httpClient
      .get<RewardResponseDTO[]>(this.buildURL(endpoint), {
        params,
      })
      .pipe(map((rewards) => rewards));
  }

  /**
   * @param {Object} queryParams
   * @return {Observable<Coupon[]>}
   */
  getCoupons(queryParams: {
    page: number;
    pageSize: number;
    active?: boolean;
    used?: boolean;
    canceled?: boolean;
    expired?: boolean;
  }): Observable<Coupon[]> {
    const endpoint = 'coupon';
    const params: any = queryParams as any;
    return this.httpClient
      .get<Coupon[]>(this.buildURL(endpoint), {
        params,
      })
      .pipe(map((coupons) => coupons));
  }

  /**
   * @param {number} rewardId
   * @return {Observable<any>}
   */
  checkRedeemable(rewardId: number): Observable<any> {
    const endpoint = `reward/redeemable/${rewardId}`;
    return this.httpClient.get<any>(this.buildURL(endpoint));
  }

  /**
   * Get Series timelines of current business that matches with reward redemption requirements
   * @param {number} rewardId
   * @return {Observable<any>}
   */
  getSeriesAvailableToRedeem(rewardId: number): Observable<any> {
    const endpoint = `reward/timeline-available/${rewardId}`;
    return this.httpClient.get<any>(this.buildURL(endpoint));
  }

  /**
   * Builds URL from endpoint
   * @param {string} endpoint
   * @return {string}
   */
  private buildURL(endpoint: string): string {
    return `${this.backendAuthURL}/${endpoint}`;
  }

  /**
   * Gets a reward service API version
   * @return {Observable<string>}
   */
  getApiVersion(): Observable<string> {
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    return this.httpClient.get(`${this.backendURL}/version`, {
      headers,
      responseType: 'text',
    });
  }
}
